
import React, { useEffect } from 'react';
import { addVersionToUrl } from '../utils/versionManager';

interface PreloadResourcesProps {
  resources?: string[];
}

/**
 * Component to preload critical resources
 * This improves performance by loading important assets ahead of time
 */
const PreloadResources: React.FC<PreloadResourcesProps> = ({ resources = [] }) => {
  useEffect(() => {
    // Default critical resources to preload
    const defaultResources = [
      "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//bible%20tools%20AI.png",
      "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//ai%20navigator.png"
    ];
    
    // Combine default and custom resources
    const allResources = [...defaultResources, ...resources];
    
    // Create link elements for preloading
    allResources.forEach(resource => {
      if (!resource) return;
      
      // Add version parameter to resource URL
      const versionedResource = addVersionToUrl(resource);
      
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = resource.match(/\.(png|jpe?g|gif|webp)$/i) ? 'image' : 'fetch';
      link.href = versionedResource;
      document.head.appendChild(link);
      
      // Preload images into browser cache
      if (link.as === 'image') {
        const img = new Image();
        img.src = versionedResource;
      }
    });
    
    // Cleanup function
    return () => {
      // Remove preload links when component unmounts
      document.querySelectorAll('link[rel="preload"]').forEach(el => {
        // Compare without version parameters
        const href = el.getAttribute('href') || '';
        const resourceMatches = allResources.some(resource => 
          href.startsWith(resource) || href.startsWith(resource.split('?')[0])
        );
        
        if (resourceMatches) {
          el.remove();
        }
      });
    };
  }, [resources]);

  // This component doesn't render anything visible
  return null;
};

export default PreloadResources;
